
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

export const useInactivityTimeout = () => {
  const timeoutRef = useRef<number | null>(null);
  const { toast } = useToast();

  const resetTimeout = () => {
    // Clear existing timeout
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = window.setTimeout(async () => {
      try {
        await supabase.auth.signOut();
        toast({
          title: "Session Expired",
          description: "You've been logged out due to inactivity",
          variant: "default"
        });
      } catch (error) {
        console.error('Error during auto-logout:', error);
      }
    }, INACTIVITY_TIMEOUT);
  };

  // Setup event listeners for user activity
  useEffect(() => {
    // Only track activity if we have a user session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;

      // Initially set the timeout
      resetTimeout();

      // Events to track for activity
      const activityEvents = [
        'mousedown', 'mousemove', 'keypress', 
        'scroll', 'touchstart', 'click', 'keydown'
      ];

      const handleActivity = () => {
        resetTimeout();
      };

      // Add event listeners
      activityEvents.forEach(event => {
        window.addEventListener(event, handleActivity);
      });

      // Cleanup
      return () => {
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current);
        }
        
        activityEvents.forEach(event => {
          window.removeEventListener(event, handleActivity);
        });
      };
    });
  }, []);

  return null;
};
